import { ReservedNodeType } from '@morph-mapper/types';
import { usePlugins } from '@morph-mapper/plugins';
import { useStore } from '@/store';
import type { StoredSuggestion, Suggestion } from '@/store';
import {
  mergeSuggestions,
  rendersSuggestionValue,
  resolveIteratorStrategy,
  suggestionCode,
  suggestionKey,
  suggestionsToRevalidate,
} from '../utils/suggestions';
import { useRenderInContext } from './use-render-in-context';

const DEFAULT_CONCURRENCY = 4;
const REBOOTSTRAP_TIMEOUT_MS = 30000;

const waitForRebootstrap = (): Promise<void> => {
  if (!useStore.getState().ai.rebootstrapInProgress) return Promise.resolve();

  return new Promise((resolve) => {
    const done = () => {
      unsubscribe();
      clearTimeout(timer);
      resolve();
    };
    const unsubscribe = useStore.subscribe((state) => {
      if (!state.ai.rebootstrapInProgress) done();
    });
    const timer = setTimeout(done, REBOOTSTRAP_TIMEOUT_MS);
  });
};

/**
 * Runs items through `task` with at most `limit` running at the same time.
 */
export const runWithLimit = async <T>(
  items: T[],
  limit: number,
  task: (item: T) => Promise<unknown>,
): Promise<void> => {
  const queue = [...items];
  const worker = async () => {
    for (let item = queue.shift(); item !== undefined; item = queue.shift()) {
      await task(item);
    }
  };
  await Promise.all(
    Array.from({ length: Math.max(1, Math.min(limit, items.length)) }, worker),
  );
};

/**
 * Evaluates fields with the plugins and keeps their suggestions in the store:
 * new suggestions, plus earlier ones that are applied or still render to
 * their value on the current document. The status per field is tracked in
 * `ai.fieldStatus`.
 */
export const useSuggestionRunner = () => {
  const { fieldSuggestions } = usePlugins();
  const renderInContext = useRenderInContext();
  const [getById, getParentId, getType, children, getTemplateValue] = useStore(
    ({ entries: e }) => [
      e.getById,
      e.getParentId,
      e.getType,
      e.children,
      e.getTemplateValue,
    ],
  );
  const [replaceSuggestions, setFieldStatus, clearStale] = useStore(
    ({ ai }) => [ai.replaceSuggestions, ai.setFieldStatus, ai.clearStale],
  );

  const entryAccess = {
    getParentId,
    getType,
    getTemplateValue,
    getIteratorId: (mapId: string) =>
      children(mapId).getId(ReservedNodeType.Iterator),
  };

  const isIteratorEntry = (entryId: string) =>
    String(getById(entryId)?.key) === ReservedNodeType.Iterator;

  const fetchField = async (entryId: string): Promise<Suggestion[]> => {
    const entry = getById(entryId);
    // The Iterator plugin answers for the #iterator entry itself. Its
    // suggestions are not tagged `iterator: true`, so asking with that flag
    // would filter all of them out.
    const result = isIteratorEntry(entryId)
      ? await fieldSuggestions({ entry, iterator: false })
      : await fieldSuggestions({
          entry,
          iterator: false,
          iteratorStrategy: resolveIteratorStrategy(entryId, entryAccess),
          renderInContext: renderInContext(entryId),
        });
    return (result ?? []) as Suggestion[];
  };

  const stillValidKeys = async (
    entryId: string,
    candidates: StoredSuggestion[],
  ): Promise<Set<string>> => {
    const valid = new Set<string>();
    if (candidates.length === 0 || isIteratorEntry(entryId)) return valid;

    const render = renderInContext(entryId);
    for (const candidate of candidates) {
      const result = await render(suggestionCode(candidate));
      if (
        result.isOk() &&
        rendersSuggestionValue(result.value, candidate.value)
      ) {
        valid.add(suggestionKey(candidate));
      }
    }
    return valid;
  };

  /**
   * `markNewest` marks what this evaluation finds as the newest
   * suggestions; only a re-evaluation should.
   */
  const evaluateField = async (
    entryId: string,
    markNewest = false,
  ): Promise<StoredSuggestion[]> => {
    setFieldStatus(entryId, 'running');
    try {
      await waitForRebootstrap();
      const fetched = await fetchField(entryId);
      const previous = useStore.getState().ai.suggestions[entryId] ?? [];
      const valid = await stillValidKeys(
        entryId,
        suggestionsToRevalidate(previous, fetched),
      );
      const merged = mergeSuggestions(previous, fetched, valid, markNewest);
      replaceSuggestions(entryId, merged);
      clearStale([entryId]);
      setFieldStatus(entryId, 'done');
      return merged;
    } catch (e) {
      setFieldStatus(
        entryId,
        'error',
        e instanceof Error ? e.message : String(e),
      );
      return useStore.getState().ai.suggestions[entryId] ?? [];
    }
  };

  const run = async (
    entryIds: string[],
    { concurrency = DEFAULT_CONCURRENCY, markNewest = false } = {},
  ) => {
    entryIds.forEach((id) => setFieldStatus(id, 'queued'));
    await runWithLimit(entryIds, concurrency, (id) =>
      evaluateField(id, markNewest),
    );
  };

  return { evaluateField, run };
};
